import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { EXPONENTS, FREQUENCY_LABELS, SUBSCRIPTION_STATUS_LABELS, apiError, formatAmount, formatDay, minorToText, toMinor, todayIso } from "../../lib/finance";
import { PayerPicker } from "./FundingRows";
import SupplierCombobox from "./SupplierCombobox";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuSeparator, ResponsiveDropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { CalendarClock, Loader2, MoreHorizontal, Plus, RefreshCw } from "lucide-react";

const selectClass = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-base md:text-sm";
const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  paused: "bg-amber-50 text-amber-800 border-amber-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
};

function StatusBadge({ status }) {
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-2xs font-medium whitespace-nowrap ${STATUS_STYLES[status]}`}>{SUBSCRIPTION_STATUS_LABELS[status]}</span>;
}

const payerText = (s) => (s.payer ? (s.payer.payer_type === "company" ? "Jamora (l'entreprise)" : s.payer.payer_label) : "Non défini");

const emptyForm = () => ({
  name: "", supplier: "", supplier_id: "", amount: "", currency: "EUR", category_id: "", frequency: "monthly", next_due_on: todayIso(),
  payer: { payer_type: "", collaborator_id: null, collaborator_label: "", third_party_name: "" }, responsible_user_id: "", note: "",
});

function formOf(s) {
  const p = s.payer || {};
  return {
    name: s.name, supplier: s.supplier || "", supplier_id: s.supplier_id || "", amount: s.amount.replace(".", ","), currency: s.currency, category_id: s.category_id,
    frequency: s.frequency, next_due_on: s.next_due_on, responsible_user_id: s.responsible_user_id, note: s.note || "",
    payer: { payer_type: p.payer_type || "", collaborator_id: p.collaborator_id || null, collaborator_label: p.payer_label || "", third_party_name: p.third_party_name || "" },
  };
}

// Declare a subscription once; a pre-filled draft then reaches its responsible at every due date.
function SubscriptionSheet({ editing, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [categories, setCategories] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(editing ? formOf(editing) : emptyForm());
    setShowErrors(false);
    key.current = crypto.randomUUID();
    Promise.all([api.get("/finance/categories"), api.get("/finance/subscriptions/responsibles")])
      .then(([c, r]) => { setCategories(c.data); setResponsibles(r.data); })
      .catch((e) => toast.error(apiError(e)));
  }, [open, editing]);

  const minor = toMinor(form.amount, form.currency);
  const dateChanged = !editing || form.next_due_on !== editing.next_due_on;
  const errors = {
    name: !form.name.trim() && "Indiquez le nom de l'abonnement.",
    amount: !minor && "Indiquez un montant positif.",
    category_id: !form.category_id && "Choisissez une catégorie.",
    next_due_on: !form.next_due_on ? "Indiquez la prochaine échéance." : dateChanged && form.next_due_on < todayIso() && "La prochaine échéance ne peut pas être dans le passé.",
    payer: form.payer.payer_type === "third_party" && !form.payer.third_party_name.trim() && "Indiquez le nom de la personne ou de l'organisation.",
    responsible_user_id: !form.responsible_user_id && "Choisissez qui complétera les brouillons.",
  };
  const invalid = Object.values(errors).some(Boolean);
  const err = (k) => showErrors && errors[k] && <p className="text-xs text-red-700">{errors[k]}</p>;
  // An existing subscription whose category was deactivated keeps it until changed.
  const categoryOptions = editing && !categories.some((c) => c.id === editing.category_id)
    ? [{ id: editing.category_id, name: editing.category_name }, ...categories] : categories;

  const save = async () => {
    setShowErrors(true);
    if (invalid) return;
    setSaving(true);
    const p = form.payer;
    const body = {
      name: form.name.trim(), supplier: form.supplier.trim() || null, supplier_id: form.supplier_id || null, amount: minorToText(minor, form.currency), currency: form.currency,
      category_id: form.category_id, frequency: form.frequency, next_due_on: form.next_due_on, responsible_user_id: form.responsible_user_id,
      note: form.note.trim() || null,
      payer: p.payer_type ? { payer_type: p.payer_type, collaborator_id: p.payer_type === "collaborator" ? p.collaborator_id : null,
        third_party_name: p.payer_type === "third_party" ? p.third_party_name.trim() : null } : null,
    };
    try {
      if (editing) await api.put(`/finance/subscriptions/${editing.id}`, { ...body, expected_version: editing.version });
      else await api.post("/finance/subscriptions", body, { headers: { "Idempotency-Key": key.current } });
      toast.success(editing ? "Abonnement modifié" : "Abonnement créé");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="finance-subscription-sheet">
        <SheetHeader>
          <SheetTitle>{editing ? "Modifier l'abonnement" : "Nouvel abonnement"}</SheetTitle>
          <SheetDescription>À chaque échéance, un brouillon pré-rempli arrive dans « À saisir » chez le responsable. Il y attend la facture.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">L'abonnement</h3>
            <div className="space-y-2">
              <Label htmlFor="sub-name">Nom *</Label>
              <Input id="sub-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} placeholder="Ex. : Claude Pro"
                className="text-base md:text-sm" data-testid="finance-subscription-name" />
              {err("name")}
            </div>
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              {/* Same field as on an expense: the drafts it creates carry this name, so it reuses the spellings already in use. */}
              <SupplierCombobox value={form.supplier} supplierId={form.supplier_id} onChange={(v) => setForm((f) => ({ ...f, ...v }))} testid="finance-subscription-supplier" />
            </div>
            <div className="grid grid-cols-[1fr_7rem] gap-3">
              <div className="space-y-2">
                <Label htmlFor="sub-amount">Montant *</Label>
                <Input id="sub-amount" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                  className="text-base md:text-sm tabular-nums" data-testid="finance-subscription-amount" />
                {err("amount")}
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub-currency">Devise</Label>
                <select id="sub-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className={selectClass}>
                  {Object.keys(EXPONENTS).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sub-category">Catégorie *</Label>
              <select id="sub-category" value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className={selectClass}
                data-testid="finance-subscription-category">
                <option value="">Choisir…</option>
                {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {err("category_id")}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Échéances</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="sub-frequency">Fréquence</Label>
                <select id="sub-frequency" value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className={selectClass}
                  data-testid="finance-subscription-frequency">
                  {Object.entries(FREQUENCY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub-next">Prochaine échéance *</Label>
                <Input id="sub-next" type="date" min={todayIso()} value={form.next_due_on} onChange={(e) => set("next_due_on", e.target.value)}
                  className="text-base md:text-sm" data-testid="finance-subscription-next" />
                {err("next_due_on")}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Qui paie, qui complète</h3>
            <div className="space-y-2">
              <Label>Payeur habituel</Label>
              <PayerPicker index={0} funding={form.payer} onPick={(patch) => set("payer", { ...form.payer, ...patch })} />
              {form.payer.payer_type === "third_party" && (
                <Input value={form.payer.third_party_name} onChange={(e) => set("payer", { ...form.payer, third_party_name: e.target.value })}
                  maxLength={120} placeholder="Nom de la personne ou de l'organisation" className="text-base md:text-sm" aria-label="Nom du tiers" />
              )}
              {err("payer")}
              <p className="text-xs text-slate-500">
                Facultatif. Le brouillon est alors pré-rempli comme payé par lui à la date d'échéance ; on corrige si besoin.
                {form.payer.payer_type && (
                  <button type="button" className="ml-1 underline" onClick={() => set("payer", emptyForm().payer)}>Retirer le payeur</button>
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sub-responsible">Responsable *</Label>
              <select id="sub-responsible" value={form.responsible_user_id} onChange={(e) => set("responsible_user_id", e.target.value)} className={selectClass}
                data-testid="finance-subscription-responsible">
                <option value="">Choisir…</option>
                {responsibles.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {err("responsible_user_id")}
              <p className="text-xs text-slate-500">Reçoit le brouillon à chaque échéance, ajoute la facture et soumet la dépense.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sub-note">Note</Label>
              <Textarea id="sub-note" value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={500} rows={2}
                placeholder="Facultatif : compte utilisé, conditions…" className="text-base md:text-sm" />
            </div>
          </section>

          <div className="flex justify-end gap-2 pb-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="finance-subscription-save">
              {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} {editing ? "Enregistrer" : "Créer l'abonnement"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ResumeDialog({ subscription, onClose, onDone }) {
  const [day, setDay] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (subscription) setDay(subscription.next_due_on >= todayIso() ? subscription.next_due_on : todayIso()); }, [subscription]);
  const invalid = !day || day < todayIso();

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/finance/subscriptions/${subscription.id}/resume`, { expected_version: subscription.version, next_due_on: day });
      toast.success("Abonnement repris");
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!subscription} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-sm w-[calc(100vw-2rem)]" data-testid="finance-subscription-resume-dialog">
        <DialogHeader>
          <DialogTitle>Reprendre « {subscription?.name} »</DialogTitle>
          <DialogDescription>Aucun brouillon n'a été créé pendant la pause. Choisissez la prochaine échéance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="sub-resume-date">Prochaine échéance</Label>
          <Input id="sub-resume-date" type="date" min={todayIso()} value={day} onChange={(e) => setDay(e.target.value)} className="text-base md:text-sm"
            data-testid="finance-subscription-resume-date" />
          {invalid && <p className="text-xs text-red-700">La prochaine échéance ne peut pas être dans le passé.</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving || invalid} data-testid="finance-subscription-resume-save">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Reprendre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EXPENSE_STATUS = { draft: "Brouillon", submitted: "Soumise" };

// Detail drawer of one subscription (same pattern as a payer in Contributions): what it is, who pays and checks it, and every expense
// it has created so far. Actions mirror the row's « … » menu.
function SubscriptionDetailSheet({ subscriptionId, onClose, canManage, canDelete, onEdit, onPause, onResume, onEnd, onDelete, onOpenExpense, refreshKey }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  useEffect(() => {
    if (!subscriptionId) return;
    let alive = true;
    setState({ status: "loading", data: null, error: "" });
    api.get(`/finance/subscriptions/${subscriptionId}`)
      .then(({ data }) => { if (alive) setState({ status: "ready", data, error: "" }); })
      .catch((e) => { if (alive) setState({ status: "error", data: null, error: apiError(e) }); });
    return () => { alive = false; };
  }, [subscriptionId, refreshKey]);
  const s = state.data;
  const row = (label, value) => value ? <><dt className="text-slate-500">{label}</dt><dd className="text-slate-800 break-words">{value}</dd></> : null;

  return (
    <Sheet open={!!subscriptionId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="finance-subscription-detail">
        <SheetHeader>
          <SheetTitle className="pr-6">{s?.name || "Abonnement"}</SheetTitle>
          <SheetDescription>{s ? [s.supplier, s.category_name].filter(Boolean).join(" · ") || "Dépense récurrente" : "Dépense récurrente"}</SheetDescription>
        </SheetHeader>
        {state.status === "loading" && <Skeleton className="h-48 w-full mt-4" />}
        {state.status === "error" && <p className="text-sm text-red-700 py-4">{state.error}</p>}
        {s && (
          <div className="space-y-5 py-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-2xl font-semibold text-slate-900 tabular-nums">{formatAmount(s.amount, s.currency)}</div>
                <div className="text-sm text-slate-500">{FREQUENCY_LABELS[s.frequency]}</div>
              </div>
              <StatusBadge status={s.status} />
            </div>
            <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-y-2 text-sm">
              {row("Prochaine échéance", s.status === "ended" ? null : formatDay(s.next_due_on))}
              {row("Dernier brouillon", s.last_generated_on ? formatDay(s.last_generated_on) : null)}
              {row("Payeur habituel", payerText(s))}
              {row("Responsable", s.responsible_name)}
              {row("En pause depuis", s.status === "paused" && s.paused_at ? formatDay(s.paused_at) : null)}
              {row("Arrêté le", s.ended_at ? formatDay(s.ended_at) : null)}
              {row("Déclaré le", s.created_at ? formatDay(s.created_at) : null)}
            </dl>
            {s.note && <p className="text-sm text-slate-700 whitespace-pre-line rounded-md bg-slate-50 p-3">{s.note}</p>}

            <div className="space-y-2">
              <div className="text-xs font-semibold text-slate-500">Dépenses créées ({s.expenses.length})</div>
              {s.expenses.length === 0 ? (
                <p className="text-sm text-slate-500">Aucune pour le moment : la première arrivera à l'échéance du {formatDay(s.next_due_on)}.</p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-md border border-slate-100" data-testid="finance-subscription-expenses">
                  {s.expenses.map((e) => {
                    const inner = (
                      <>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-slate-900 truncate">Échéance du {formatDay(e.due_on)}</span>
                          <span className="flex flex-wrap gap-1 mt-0.5">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-2xs text-slate-700">{EXPENSE_STATUS[e.status] || e.status}</span>
                            {e.receipt_missing && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-2xs text-amber-900">Justificatif manquant</span>}
                          </span>
                        </span>
                        <span className="text-sm font-medium tabular-nums text-slate-900">{formatAmount(e.amount, e.currency)}</span>
                      </>
                    );
                    return (
                      <li key={e.id}>
                        {onOpenExpense ? (
                          <button type="button" onClick={() => onOpenExpense(e.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50">{inner}</button>
                        ) : <div className="flex items-center gap-3 px-3 py-2">{inner}</div>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {canManage && s.status !== "ended" && (
              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <Button variant="outline" onClick={() => onEdit(s)} data-testid="finance-subscription-detail-edit">Modifier</Button>
                {s.status === "active" && <Button variant="outline" onClick={() => onPause(s)}>Mettre en pause</Button>}
                {s.status === "paused" && <Button variant="outline" onClick={() => onResume(s)}>Reprendre</Button>}
                <Button variant="outline" className="text-red-700" onClick={() => onEnd(s)}>Arrêter l'abonnement</Button>
                {canDelete && <Button variant="outline" className="text-red-700" onClick={() => onDelete(s)} data-testid="finance-subscription-detail-delete">Supprimer…</Button>}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function SubscriptionsPanel({ canManage, onOpenExpense = null, initialOpenId = null, startCreate = false }) {
  const [state, setState] = useState({ status: "loading", items: [], error: "" });
  const [sheet, setSheet] = useState({ open: false, editing: null });
  const [resuming, setResuming] = useState(null);
  const [ending, setEnding] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { user } = useAuth();
  const canDelete = canManage && hasPermission(user, "finance:delete");
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Opened from the global search: a given subscription, or the creation drawer.
  useEffect(() => { if (initialOpenId) setOpenId(initialOpenId); }, [initialOpenId]);
  useEffect(() => { if (startCreate && canManage) setSheet({ open: true, editing: null }); }, [startCreate, canManage]);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => { load(); setRefreshKey((k) => k + 1); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load is stable

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const { data } = await api.get("/finance/subscriptions");
      setState({ status: "ready", items: data, error: "" });
    } catch (e) {
      setState({ status: "error", items: [], error: apiError(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (s, action, message) => {
    setBusy(true);
    try {
      await api.post(`/finance/subscriptions/${s.id}/${action}`, { expected_version: s.version });
      toast.success(message);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const actions = (s) => s.status === "ended" ? null : (
    <ResponsiveDropdownMenu>
      <ResponsiveDropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions pour ${s.name}`} data-testid="finance-subscription-actions"><MoreHorizontal size={16} /></Button>
      </ResponsiveDropdownMenuTrigger>
      <ResponsiveDropdownMenuContent align="end" title={s.name}>
        <ResponsiveDropdownMenuItem onClick={() => setSheet({ open: true, editing: s })}>Modifier</ResponsiveDropdownMenuItem>
        {s.status === "active" && <ResponsiveDropdownMenuItem disabled={busy} onClick={() => act(s, "pause", "Abonnement mis en pause")} data-testid="finance-subscription-pause">Mettre en pause</ResponsiveDropdownMenuItem>}
        {s.status === "paused" && <ResponsiveDropdownMenuItem onClick={() => setResuming(s)} data-testid="finance-subscription-resume">Reprendre</ResponsiveDropdownMenuItem>}
        <ResponsiveDropdownMenuSeparator />
        <ResponsiveDropdownMenuItem className="text-red-700" onClick={() => setEnding(s)} data-testid="finance-subscription-end">Arrêter l'abonnement</ResponsiveDropdownMenuItem>
        {canDelete && <ResponsiveDropdownMenuItem className="text-red-700" onClick={() => setDeleting(s)} data-testid="finance-subscription-delete">Supprimer…</ResponsiveDropdownMenuItem>}
      </ResponsiveDropdownMenuContent>
    </ResponsiveDropdownMenu>
  );

  const items = state.items;
  return (
    <section className="space-y-4" aria-label="Abonnements" data-testid="finance-subscriptions">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm text-slate-600 flex-1">Les dépenses récurrentes : à chaque échéance, un brouillon pré-rempli attend la facture chez son responsable.</p>
        {canManage && (
          <Button onClick={() => setSheet({ open: true, editing: null })} data-testid="finance-subscription-new">
            <Plus size={15} className="mr-1.5" /> Nouvel abonnement
          </Button>
        )}
      </div>

      {state.status === "loading" && !items.length ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : state.status === "error" ? (
        <Card className="p-5 text-center space-y-2">
          <p className="text-sm text-slate-600">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Réessayer</Button>
        </Card>
      ) : !items.length ? (
        <Card className="p-6 text-center space-y-1">
          <CalendarClock className="mx-auto text-slate-400" size={22} />
          <h3 className="font-display font-semibold text-slate-900">Aucun abonnement déclaré</h3>
          <p className="text-sm text-slate-500">Déclarez vos frais récurrents (Claude Pro, hébergement, domaines) pour ne plus oublier de les saisir.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="hidden md:table w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr><th className="px-3 py-2.5">Abonnement</th><th className="px-3 py-2.5 text-right">Montant</th><th className="px-3 py-2.5">Prochaine échéance</th>
                <th className="px-3 py-2.5">Payeur habituel</th><th className="px-3 py-2.5">Responsable</th><th className="px-3 py-2.5">Statut</th><th className="w-10" /></tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} onClick={() => setOpenId(s.id)} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${s.status === "ended" ? "text-slate-500" : ""}`}
                  data-testid="finance-subscription-row">
                  <td className="px-3 py-2.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setOpenId(s.id); }} className="text-left font-medium text-slate-900 hover:text-jamora hover:underline">{s.name}</button><div className="text-xs text-slate-500">{[s.supplier, s.category_name].filter(Boolean).join(" · ")}</div></td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatAmount(s.amount, s.currency)}<div className="text-xs text-slate-500">{FREQUENCY_LABELS[s.frequency]}</div></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{s.status === "ended" ? "Aucune" : formatDay(s.next_due_on)}</td>
                  <td className="px-3 py-2.5">{payerText(s)}</td>
                  <td className="px-3 py-2.5">{s.responsible_name}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
                  <td className="px-1 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>{canManage && actions(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.id} className="p-3 flex gap-3" data-testid="finance-subscription-card">
                <button type="button" onClick={() => setOpenId(s.id)} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-2"><span className="font-medium text-slate-900 break-words">{s.name}</span><StatusBadge status={s.status} /></div>
                  <div className="text-sm tabular-nums">{formatAmount(s.amount, s.currency)} · {FREQUENCY_LABELS[s.frequency]}</div>
                  <div className="text-xs text-slate-500">
                    {s.status !== "ended" && `Prochaine échéance : ${formatDay(s.next_due_on)} · `}Responsable : {s.responsible_name}
                  </div>
                </button>
                {canManage && actions(s)}
              </div>
            ))}
          </div>
        </Card>
      )}

      <SubscriptionDetailSheet subscriptionId={openId} onClose={() => setOpenId(null)} canManage={canManage} canDelete={canDelete} refreshKey={refreshKey}
        onEdit={(s) => setSheet({ open: true, editing: s })} onPause={(s) => act(s, "pause", "Abonnement mis en pause")} onResume={setResuming} onEnd={setEnding} onDelete={setDeleting}
        onOpenExpense={onOpenExpense ? (id) => { setOpenId(null); onOpenExpense(id); } : null} />
      {canManage && <SubscriptionSheet open={sheet.open} editing={sheet.editing} onOpenChange={(o) => setSheet((x) => ({ ...x, open: o }))} onSaved={reload} />}
      {canManage && <ResumeDialog subscription={resuming} onClose={() => setResuming(null)} onDone={reload} />}
      <DeleteWithReasonDialog
        target={deleting} testId="delete-subscription" onClose={() => setDeleting(null)}
        title={`Supprimer l'abonnement « ${deleting?.name || ""} » ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un abonnement qui a déjà créé des brouillons ne peut pas être supprimé : arrêtez-le à la place."
        onConfirm={async (reason) => {
          const target = deleting;
          try {
            await api.delete(`/finance/subscriptions/${target.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            setDeleting(null);
            setOpenId(null);
            reload();
          } catch (e) {
            setDeleting(null);
            toast.error(apiError(e));
          }
        }}
      />
      <AlertDialog open={!!ending} onOpenChange={(o) => { if (!o) setEnding(null); }}>
        <AlertDialogContent data-testid="finance-subscription-end-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Arrêter « {ending?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Plus aucun brouillon ne sera créé. Les dépenses déjà créées restent. Un abonnement arrêté ne peut pas être repris : il faudra en déclarer un nouveau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Retour</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" data-testid="finance-subscription-end-confirm"
              onClick={() => { const s = ending; setEnding(null); act(s, "end", "Abonnement arrêté"); }}>
              Arrêter l'abonnement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// « À saisir »: the drafts generated for the signed-in responsible, waiting for their invoice.
export function SubscriptionDrafts({ items, onOpen }) {
  if (!items.length) return null;
  return (
    <Card className="p-4 space-y-3" data-testid="finance-subscription-drafts">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Abonnements à compléter</h3>
        <p className="text-xs text-slate-500">Brouillons créés à l'échéance. Ajoutez la facture, vérifiez le paiement, puis soumettez.</p>
      </div>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {items.map((d) => (
          <li key={d.id} className="p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="finance-subscription-draft">
            <span className="min-w-0 flex-1">
              <span className="block text-slate-900 break-words">{d.title}</span>
              <span className="block text-xs text-slate-500">Échéance du {formatDay(d.due_on)}{d.receipt_missing && " · facture à ajouter"}</span>
            </span>
            <strong className="tabular-nums whitespace-nowrap">{formatAmount(d.amount, d.currency)}</strong>
            <Button size="sm" variant="outline" onClick={() => onOpen(d)} data-testid="finance-subscription-draft-open">Compléter</Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
